import React from "react";
import { motion } from "framer-motion";
import { Images, Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { WhatsappIcon } from "@/components/Navbar";
import { PORTFOLIOS } from "@/data/solutions";
import { buildWhatsappLink } from "@/lib/brand";

export default function PortfolioModal({ type = "sites" }) {
  const config = PORTFOLIOS[type];

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          data-testid={`open-portfolio-button-${type}`}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-efory-blue mt-4 hover:gap-2.5 transition-all duration-300"
        >
          <Images className="h-4 w-4" /> {config.triggerLabel}
        </button>
      </DialogTrigger>
      <DialogContent
        data-testid={`portfolio-modal-${type}`}
        className="max-w-5xl bg-efory-ink border-white/10 text-white p-0 overflow-hidden gap-0"
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-white/10 text-left">
          <DialogTitle className="font-heading text-2xl font-extrabold text-white">
            {config.title} <span className="text-efory-blue">{config.titleHighlight}</span>
          </DialogTitle>
          <DialogDescription className="text-white/60">{config.description}</DialogDescription>
        </DialogHeader>

        <div className="max-h-[70vh] overflow-y-auto px-6 py-6">
          <div className="grid sm:grid-cols-2 gap-5">
            {config.items.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                data-testid={`portfolio-item-${p.id}`}
                className={`group rounded-2xl overflow-hidden border border-white/10 bg-white/[0.03] hover:border-efory-blue/40 transition-all duration-300 ${
                  config.items.length === 3 && i === 0 ? "sm:col-span-2" : ""
                }`}
              >
                <div className="relative overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.title}
                    loading="lazy"
                    className="w-full aspect-[3/2] object-cover object-center transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
                <div className="p-4">
                  <span className="text-xs font-semibold uppercase tracking-[0.15em] text-efory-blue">
                    {p.category}
                  </span>
                  <h4 className="font-heading text-lg font-bold mt-1 text-white">{p.title}</h4>
                </div>
              </motion.div>
            ))}
          </div>

          {config.features && (
            <div className="mt-8">
              <h4 className="font-heading text-lg font-bold text-white">
                O que está <span className="text-efory-blue">incluso</span>
              </h4>
              <div
                data-testid={`portfolio-features-${type}`}
                className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3"
              >
                {config.features.map((f) => (
                  <div key={f} className="flex items-start gap-2.5 text-sm">
                    <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full bg-efory-blue/15 flex items-center justify-center">
                      <Check className="h-3.5 w-3.5 text-efory-blue" />
                    </span>
                    <span className="text-white/75 leading-snug">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-white/10 px-6 py-5 bg-white/[0.02]">
          <p className="text-sm text-white/70 text-center sm:text-left">
            Gostou do que viu?{" "}
            <span className="text-white font-semibold">{config.footerText}</span>
          </p>
          <a
            href={buildWhatsappLink(config.ctaMessage)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`portfolio-cta-whatsapp-${type}`}
            className="w-full sm:w-auto"
          >
            <Button className="w-full bg-[#25D366] hover:bg-[#1fb959] text-white rounded-full px-6 font-semibold transition-all duration-300 hover:scale-[1.03] whitespace-nowrap">
              <WhatsappIcon className="mr-2 h-5 w-5" /> {config.ctaLabel}
            </Button>
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
}
